import customElement from "@/wc/registry/lib/decorator/customElement/index.js";
import StekzFormInputElement from "@/wc/registry/component/form-input-base/index.js";
import type IFormInputTypeComponent from "@/wc/registry/types/form-input-type";

import serviceRepository from "@/wc/registry/lib/service/repository/index.js";
import * as dom from "@/wc/registry/lib/dom/index.js";
import * as form from "@/wc/registry/lib/form/index.js";

// Not using a shadowRoot, to secretly make use of the external css
// utilized by c-form-input. (to avoids fetching the same css bundle
// multiple times per c-form-input-[type])
const componentName = "c-form-input-choice";
@customElement(componentName, {
    style: fetch("/wc/registry/component/form-input-choice/styles.css").then(
        (res) => res.text()
    ),
    template: fetch(
        "/wc/registry/component/form-input-choice/template.html"
    ).then((res) =>
        res.text().then((html) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            return doc.querySelector(`[data-component=${componentName}]`);
        })
    ),
    shadowRoot: false,
})
export default class FormInputChoice
    extends StekzFormInputElement
    implements IFormInputTypeComponent
{
    usedProps = [
        "value",
        "label",
        "required",
        "readOnly",
        "helperText",
        "horizontal",
        "prependValue",
        "appendValue",
        "parseType",
        "inline",
    ];

    isInline = false;

    static get observedAttributes() {
        return [
            "data-value",
            "data-choices",
            "data-read-only",
            "data-prepend-value",
            "data-append-value",
            "data-parse-type",
        ];
    }

    constructor() {
        super();

        // Similar to what is being done in the StekzHTMLElement constructor,
        // check it out for its documentation.
        this.nodes = dom.NodesProxy(this);
    }

    // When the value attribute is changed, be sure to update the nested nodes
    // to reflect this change.
    attributeChangedCallback(
        name: string,
        _oldValue: string,
        newValue: string
    ) {
        if (name === "data-value" && this.nodes.selector) {
            this.nodes.selector.value = newValue;
        }

        if (name === "data-name") {
            this.updateContextIfNeeded();
        }

        if (name === "data-value") {
            this.updateContextIfNeeded();
            this.setNullableOption(newValue);
        }

        if (name === "data-prepend-value" && this.nodes.selector) {
            this.prependValue = newValue;
        }
        if (name === "data-append-value" && this.nodes.selector) {
            this.appendValue = newValue;
        }

        if (name === "data-choices" && this.nodes.selector) {
            this.setChoiceOptions();
        }
        if (name === "data-read-only" && this.nodes.selector) {
            if (newValue === "undefined") {
                this.nodes.selector.removeAttribute("disabled");
            } else {
                this.readOnly = newValue;
            }
        }
    }

    setNullableOption(value) {
        // Nothing to do if there is no selector in the dom.
        if (!this.nodes.selector) {
            return;
        }

        // There should be no nullable choice if the field is required
        if (this.isRequired()) {
            return;
        }

        const nullableOption =
            this.nodes.selector.querySelector(".nullable-option");
        // If there is no value selected, then remove the nullable option
        if (nullableOption && !value) {
            nullableOption.remove();
            return;
        }

        // If the value is null (or falsy), no need to add the nullable option.
        if (!value) {
            return;
        }

        // If there already is a nullable option, no need to add another one.
        if (nullableOption) {
            return;
        }

        // If there is a value selected, then add the nullable option
        const nullableOptionElement = document.createElement("option");
        nullableOptionElement.innerHTML = "-";
        nullableOptionElement.setAttribute("value", "");
        nullableOptionElement.classList.add("nullable-option");
        this.nodes.selector
            .querySelector(".default-option")
            ?.after(nullableOptionElement);
    }

    handleEvent(event) {
        this.value = event.target.value;
        this.reset();
    }

    updateContextIfNeeded() {
        // When data-use-as-context is being set, update the repository context.
        // Probably there is some other component that is utilizing a repository
        // that needs this specific key+value set before it is able to do a call
        // to the backend.
        if (this.useAsContext && this.value && this.dataset.name) {
            serviceRepository.context.update({
                [this.dataset.name]: this.value,
            });
        }
    }

    set name(value: string) {
        this.dataset.name = value;
    }

    // When data-use-as-context is set, the chosen value will be saved to the
    // repository context. E.g. when the data-name of this element is
    // 'property_id' and the chosen value is '5', {'property_id': 5} will be
    // saved to the repository context.
    get useAsContext() {
        return (
            this.dataset.useAsContext === "" ||
            this.dataset.useAsContext === "true"
        );
    }

    set inline(value: string) {
        this.isInline = value !== "false" && value !== undefined;
    }

    set readOnly(value: string) {
        if (!this.nodes.selector) {
            return;
        }

        if (value === undefined || value === "null") {
            this.nodes.selector.removeAttribute("disabled");
        } else {
            this.nodes.selector.setAttribute("disabled", "");
        }
    }

    set horizontal(value: string) {
        if (value !== undefined) {
            this.nodes["field-wrapper"].classList.add("field-wrapper");
            this.nodes["label-wrapper"].classList.add("label-wrapper");
        }
    }

    set prependValue(value: string) {
        if (value !== undefined) {
            if (this.querySelector(".append-prepend-wrapper")) {
                const childElement = this.querySelector(".prepend-element");
                childElement.innerText = value;
            } else {
                const prependWrapper = document.createElement("div");
                prependWrapper.classList.add("append-prepend-wrapper");
                const childElement = document.createElement("div");
                childElement.innerText = value;
                childElement.classList.add("prepend-element");
                prependWrapper.appendChild(childElement);
                prependWrapper.appendChild(this.nodes.select);
                this.nodes.control.insertAdjacentElement(
                    "afterbegin",
                    prependWrapper
                );
            }
        }
    }
    set appendValue(value: string) {
        if (value !== undefined) {
            if (this.querySelector(".append-prepend-wrapper")) {
                const childElement = this.querySelector(".append-element");
                childElement.innerText = value;
            } else {
                const appendWrapper = document.createElement("div");
                appendWrapper.classList.add("append-prepend-wrapper");
                const childElement = document.createElement("div");
                childElement.innerText = value;
                childElement.classList.add("append-element");
                appendWrapper.appendChild(this.nodes.select);
                appendWrapper.appendChild(childElement);
                this.nodes.control.insertAdjacentElement(
                    "afterbegin",
                    appendWrapper
                );
            }
        }
    }

    set value(value: string) {
        // Sometimes this value is set before the template is rendered, in that
        // case, store the value in the attribute so it can be used later.
        this.dataset.value = value || "";

        // Let the form know that we've changed our value. It might be that there
        // are other form-inputs depending on the value of this form-input.
        // The form will then inform the right c-form-inputs that they have to
        // re-fetch their values.
        this.dispatchEvent(
            new CustomEvent("c-form-input:changed", {
                bubbles: true,
                composed: true,
                detail: {
                    label: this.dataset.label,
                    value: this.value,
                    name: this.dataset.name,
                },
            })
        );
        this.dispatchEvent(
            new CustomEvent("c-form-input-choice:changed", {
                bubbles: true,
                composed: true,
                detail: {
                    label: this.dataset.label,
                    value: this.value,
                    name: this.dataset.name,
                },
            })
        );
    }

    get value(): string | number | boolean | null {
        let parseType = this.dataset.parseType;
        if (parseType === undefined) {
            parseType = "string";
        }
        let value = this.dataset.value;
        if (this.nodes.selector && this.nodes.selector.value) {
            value = this.nodes.selector.value;
        }

        if (!value) {
            return null;
        }
        if (parseType === "number") {
            return Number(value);
        } else if (parseType === "boolean") {
            return value.toLowerCase() === "true";
        }
        return value;
    }

    set helperText(value: string) {
        if (this.nodes.select && value) {
            const errorElement = this.nodes["help-text"];

            const pElement = document.createElement("p");
            pElement.classList.add("help", "has-text-grey", "mb-0");
            pElement.innerText = value;

            errorElement.parentNode.insertBefore(pElement, errorElement);
        }
    }

    get key() {
        return this.dataset.key || "id";
    }

    get keyDescription() {
        return this.dataset.keyDescription || "name";
    }

    get shouldSort() {
        return this.dataset.avoidSort === undefined;
    }

    get serviceRepository() {
        return this.dataset.serviceRepository;
    }

    set required(value: string) {
        if (this.nodes.required && value !== undefined) {
            this.nodes.required.classList.add("required");
        } else {
            this.nodes.required.classList.add("not-required");
        }
    }

    isRequired(): boolean {
        return this.dataset.required !== undefined;
    }

    // Retrieve all the options that can be selected in our component. These
    // can be either retrieved dynamically through a repository or simply
    // through data-choices.
    getOptions(): Promise<[{ value: string | number; name: string }[]?]> {
        const options = this.dataset.choices;
        if (options) {
            return JSON.parse(options);
        }

        // When options is not defined, but the service-repository is, fetch &
        // list the options given by the repository.
        if (this.serviceRepository) {
            const repository = serviceRepository.get(this.serviceRepository);

            let action = "all";
            // If a given repository only supports one type of action, use that
            // action instead. This is useful to support openapi-generated
            // repositories where the endpoints are not crud-like.
            if (Object.keys(repository.actionPaths).length === 1) {
                action = Object.keys(repository.actionPaths)[0];
            }

            const requiredParameters =
                repository.actionPaths[action].requiredParameters;
            const parameters = dom.extractRepositoryParametersFromElement(
                this,
                requiredParameters
            );
            // Add the parameters or query parameters here so that we could use
            // them in the repository modifications when needed.
            parameters["_extra"] = { current_value: this.value };
            // When form-input depends on another form-input, chances are that
            // a required parameter is not yet set.
            //
            // This might stand in the way of getting the options from a repository,
            // because to get options from a repository a request might need to be
            // done to e.g. /producer/{producer_id}/producer-locations, which
            // requires the 'producer_id' parameter to be set. That parameter
            // is set (and present within the 'localIDs' variable), because it is
            // set by c-form after the c-form-input that this form input depends
            // on has a selected value.
            const failSilently = "dependsOn" in this.dataset;
            return form.getOptionsFromRepository(
                this.serviceRepository,
                this.key,
                this.keyDescription,
                parameters,
                failSilently
            );
        }
    }

    reset() {
        this.error = "";
    }

    showErrorIndicators(value: string) {
        if (value) {
            this.nodes.control.setAttribute("class", "control has-icons-right");
            this.nodes.select.setAttribute("class", "select is-danger");
        } else {
            this.nodes.select.setAttribute("class", "select");
        }
    }

    updateErrorText(errorText: string) {
        super.updateErrorText(errorText);
        if (errorText) {
            dom.show(this.nodes["help-text"]);
        } else {
            this.updateInline();
        }
    }

    updateInline() {
        if (!this.isConnected || !this.target) {
            return;
        }

        const showOrHide = this.isInline ? dom.hide : dom.show;
        showOrHide(this.nodes["label-wrapper"]);
        showOrHide(this.nodes["help-text"]);
    }

    // Render all the values to the template by iterating over all 'data-<name>'
    // attributes. Optionally skips setting the choice options, e.g. when the
    // form-input first requires more info to be able to determine these choice
    // options. This is typically determined through data-depends-on.
    updateValues(options?: { skipSettingChoiceOptions?: boolean }) {
        const skipSettingChoiceOptions = options
            ? options.skipSettingChoiceOptions
            : undefined;

        this.usedProps.forEach((prop) => {
            this[prop] = this.dataset[prop];
        });

        this.clearOptions();
        this.setDefaultChoiceOption();
        if (skipSettingChoiceOptions) {
            return;
        }

        this.setChoiceOptions();
    }

    clearOptions() {
        if (this.nodes.selector) {
            dom.empty(this.nodes.selector);
        }
    }

    setDefaultChoiceOption() {
        // set default option value for select
        const defaultOptionElement = document.createElement("option");
        defaultOptionElement.innerHTML =
            this.dataset.placeholder || "Select an option";
        defaultOptionElement.setAttribute("value", "");
        defaultOptionElement.setAttribute("disabled", "");
        defaultOptionElement.classList.add("default-option");

        if (this.value === null) {
            defaultOptionElement.setAttribute("selected", "");
        }
        this.nodes.selector.appendChild(defaultOptionElement);
        this.setNullableOption(this.value);
    }

    async setChoiceOptions() {
        // convert string to HTML element
        let options = await this.getOptions();
        if (this.shouldSort && options) {
            options = options.sort((a, b) => {
                if (typeof a.name === "number" && typeof b.name === "number") {
                    return a.name > b.name ? 1 : -1;
                }
                return a.name.localeCompare(b.name);
            });
        }

        this.clearOptions();
        this.setDefaultChoiceOption();
        if (options === undefined) {
            return;
        }

        options.forEach((val) => {
            const optionElement = document.createElement("option");
            val && optionElement.setAttribute("value", val.value);
            optionElement.innerHTML = val.name;
            this.nodes.selector.appendChild(optionElement);
        });

        this.selectOptionIfPossible(options);
        this.dispatchEvent(
            new CustomEvent("c-form-input:populated", {
                bubbles: true,
                composed: true,
            })
        );
    }

    selectOptionIfPossible(options) {
        if (this.dataset.value === null || this.dataset.value === "") {
            return;
        }

        const toSelect = options.find(
            (option) => `${option.value}` === this.dataset.value
        );
        if (toSelect) {
            this.nodes.selector.value = this.dataset.value;
        }
    }

    async connectedCallback() {
        super.connectedCallback();
        // In some cases, a choice field requires some parameters to be set
        // through a different choice field. There might be 2 form inputs:
        //  1. producer id
        //  2. producer location id
        // Where the second form input is a choice field, which requires the
        // value from the first form input to retrieve its options, because
        // a GET to list the producer locations requires a producer id in the
        // request url (e.g. /producer/{producer_id}/locations). In this case,
        // the second form input can't retrieve locations without a producer id.
        //
        // Because of this, we won't set the choice options ourselves. We'll rely
        // on the parent form to set the correct params and call updateValues
        // when it deems us ready.
        let skipSettingChoiceOptions = false;
        if ("dependsOn" in this.dataset) {
            skipSettingChoiceOptions = true;
        }

        this.updateValues({ skipSettingChoiceOptions });
        this.updateInline();

        this.nodes.selector.addEventListener("input", this);
    }

    disconnectedCallback() {
        this.nodes.selector.removeEventListener("input", this);
        this.updateContextIfNeeded();

        if (this.useAsContext) {
            serviceRepository.context.remove([this.dataset.name]);
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "c-form-input-choice": FormInputChoice;
    }
}
